import { Camera } from 'lucide-react';
import { ChangeEvent, KeyboardEvent, RefObject, useEffect, useRef } from 'react';

import Link from 'next/link';

import { ROUTES } from '@shared/constants/routes';
import { Button } from '@shared/ui/Button';

import { ReviewImagesPreview } from './ReviewImagesPreview';
import { ReviewFormStars } from './ReviewStars';
import cls from './Reviews.module.scss';

type ReviewFormProps = {
  selectedRate: number;
  hoveredRate: number;
  reviewText: string;
  previewUrls: string[];
  isSubmitDisabled: boolean;
  isSubmittingReview: boolean;
  fileInputRef: RefObject<HTMLInputElement>;
  onChangeRate: (rate: number) => void;
  onChangeHoveredRate: (rate: number) => void;
  onChangeFiles: (event: ChangeEvent<HTMLInputElement>) => void;
  onChangeReviewText: (value: string) => void;
  onClickPhotoButton: () => void;
  onRemoveFile: (index: number) => void;
  onSubmit: () => void;
  onOpenPublicationRules: () => void;
};

export const ReviewForm = ({
  selectedRate,
  hoveredRate,
  reviewText,
  previewUrls,
  isSubmitDisabled,
  isSubmittingReview,
  fileInputRef,
  onChangeRate,
  onChangeHoveredRate,
  onChangeFiles,
  onChangeReviewText,
  onClickPhotoButton,
  onRemoveFile,
  onSubmit,
  onOpenPublicationRules
}: ReviewFormProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resizeTextarea = () => {
    const textarea = textareaRef.current;

    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const handleChangeReviewText = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onChangeReviewText(event.target.value);
    resizeTextarea();
  };

  const handlePublicationRulesKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    onOpenPublicationRules();
  };

  useEffect(() => {
    resizeTextarea();
  }, [reviewText]);

  return (
    <>
      <div className={cls.formControls}>
        <div className={cls.ratingButton}>
          <ReviewFormStars
            selectedRate={selectedRate}
            hoveredRate={hoveredRate}
            onChangeRate={onChangeRate}
            onChangeHoveredRate={onChangeHoveredRate}
          />
        </div>
        <button
          className={cls.photoButton}
          type={'button'}
          onClick={onClickPhotoButton}
        >
          <Camera
            width={24}
            height={24}
          />
          <span>Добавить фотографии</span>
        </button>
        <input
          ref={fileInputRef}
          className={cls.fileInput}
          type={'file'}
          accept={'image/*'}
          multiple
          onChange={onChangeFiles}
        />
      </div>

      <ReviewImagesPreview
        previewUrls={previewUrls}
        onRemove={onRemoveFile}
      />

      <textarea
        ref={textareaRef}
        className={cls.textarea}
        placeholder={'Поделитесь своим мнением'}
        value={reviewText}
        onChange={handleChangeReviewText}
      />

      <Button
        className={cls.submitButton}
        size={'l'}
        fullWidth
        disabled={isSubmitDisabled}
        isLoading={isSubmittingReview}
        loadingButton={isSubmittingReview}
        onClick={onSubmit}
      >
        Оставить отзыв
      </Button>

      <p className={cls.agreement}>
        Оставляя отзыв, вы соглашаетесь c{' '}
        <span
          className={cls.agreementLink}
          role={'button'}
          tabIndex={0}
          onClick={onOpenPublicationRules}
          onKeyDown={handlePublicationRulesKeyDown}
        >
          правилами публикации пользовательского контента
        </span>{' '}
        и даете свое{' '}
        <Link
          className={cls.agreementLink}
          href={ROUTES.CONSENT_PERSONAL_DATA}
        >
          согласие на обработку персональных данных
        </Link>
        .
      </p>
    </>
  );
};
